import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import Logo from '@cartwhl/ui/atoms/Logo';
import Text from '@cartwhl/ui/base/Text';
import useElementSize from '@cartwhl/ui/hooks/useElementSize';
import UserMenu from '@cartwhl/ui/organisms/UserMenu';

import { userStore } from 'stores/global';

const CANVAS_LAYOUT_HEADER_SECTION_GAP_PX = 16;

interface CanvasLayoutHeaderProps {
  title: string;
  subtitle?: string;
  hasHeaderContent?: boolean;
  onStartHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onCenterHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onEndHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
}

export default function CanvasLayoutHeader({
  title,
  subtitle,
  hasHeaderContent = false,
  onStartHeaderContentContainerChange,
  onCenterHeaderContentContainerChange,
  onEndHeaderContentContainerChange,
}: CanvasLayoutHeaderProps) {
  const { loading: userLoading } = useSnapshot(userStore.state);
  const headerRef = useRef<HTMLElement>(null);
  const startGroupRef = useRef<HTMLDivElement>(null);
  const endGroupRef = useRef<HTMLDivElement>(null);
  const { width: headerWidth } = useElementSize(headerRef);
  const { width: startGroupWidth } = useElementSize(startGroupRef);
  const { width: endGroupWidth } = useElementSize(endGroupRef);
  const centerLaneStartPx = startGroupWidth + CANVAS_LAYOUT_HEADER_SECTION_GAP_PX;
  const centerLaneWidthPx = Math.max(
    0,
    headerWidth -
      centerLaneStartPx -
      endGroupWidth -
      CANVAS_LAYOUT_HEADER_SECTION_GAP_PX,
  );

  return (
    <header
      ref={headerRef}
      className="bg-primary relative flex h-16 shrink-0 items-center justify-between gap-4 px-4 lg:px-6"
    >
      <div ref={startGroupRef} className="flex min-w-0 items-end gap-[22px]">
        <div
          ref={onStartHeaderContentContainerChange}
          className="mb-[-3px] flex shrink-0 items-center empty:hidden"
        />
        <Logo className="text-primary h-5 w-auto shrink-0" />
        <Text
          as="h1"
          variant="studio/title/lg"
          className="text-primary mb-[-3px] h-5 truncate"
        >
          {title}

          {subtitle && !hasHeaderContent && (
            <small className="text-tertiary ml-2 hidden h-full font-sans text-sm font-medium md:inline">
              {subtitle}
            </small>
          )}
        </Text>
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 flex items-center overflow-hidden"
        style={{
          left: centerLaneStartPx,
          width: centerLaneWidthPx,
        }}
      >
        <div
          ref={onCenterHeaderContentContainerChange}
          className="pointer-events-auto mb-[-3px] flex min-w-0 w-full items-end justify-center overflow-hidden"
        />
      </div>
      <div ref={endGroupRef} className="flex min-w-0 items-center gap-4">
        <div
          ref={onEndHeaderContentContainerChange}
          className="flex min-w-0 items-center justify-end overflow-hidden"
        />
        <div className="shrink-0">{!userLoading && <UserMenu />}</div>
      </div>
    </header>
  );
}
